import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Trophy, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const BadgesPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [allBadges, setAllBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBadgesData();
  }, []);

  const loadBadgesData = async () => {
    try {
      const token = localStorage.getItem('spirify_token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const [availableRes, myBadgesRes, statsRes] = await Promise.all([
        fetch(`${window.location.origin}/api/gamification/badges/available`),
        fetch(`${window.location.origin}/api/gamification/badges/my-badges`, { headers }),
        fetch(`${window.location.origin}/api/gamification/stats`, { headers })
      ]);

      const [available, earned, stats] = await Promise.all([
        availableRes.json(),
        myBadgesRes.json(),
        statsRes.json()
      ]);

      setAllBadges(available.badges || []);
      setMyBadges(earned.badges || []);
      setUserStats(stats);
      setLoading(false);
    } catch (error) {
      console.error('Error loading badges:', error);
      setLoading(false);
    }
  };

  const isBadgeEarned = (badgeId) => {
    return myBadges.some(badge => badge.id === badgeId);
  };

  const getBadgeProgress = (badge) => {
    if (!userStats) return 0;

    const criteria = badge.criteria;
    
    if (criteria.lessons_completed) {
      return Math.min(100, (userStats.lessons_completed / criteria.lessons_completed) * 100);
    }
    if (criteria.xp) {
      return Math.min(100, (userStats.total_xp / criteria.xp) * 100);
    }
    if (criteria.streak) {
      return Math.min(100, (userStats.current_streak / criteria.streak) * 100);
    }
    if (criteria.perfect_lessons) {
      return Math.min(100, (userStats.perfect_lessons || 0) / criteria.perfect_lessons * 100);
    }

    return 0;
  };

  const getCategoryTitle = (category) => {
    const titles = {
      'progression': language === 'fr' ? 'Progression' : 'Progression',
      'streak': language === 'fr' ? 'Régularité' : 'Consistency',
      'xp': language === 'fr' ? 'Points d\'expérience' : 'Experience Points',
      'performance': language === 'fr' ? 'Performance' : 'Performance',
      'completion': language === 'fr' ? 'Accomplissement' : 'Completion'
    };
    return titles[category] || category;
  };

  const groupedBadges = allBadges.reduce((acc, badge) => {
    const category = badge.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(badge);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-xl">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Button>
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
              <Trophy className="w-10 h-10 text-yellow-500" />
              {language === 'fr' ? 'Mes Badges' : 'My Badges'}
            </h1>
            <p className="text-gray-600 mt-2">
              {myBadges.length} / {allBadges.length} {language === 'fr' ? 'badges débloqués' : 'badges unlocked'}
            </p>
          </div>

          <div className="w-24"></div>
        </div>

        {/* Progress Overview */}
        <Card className="mb-8 bg-white/80 backdrop-blur border-2 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex justify-around">
              <div className="text-center">
                <p className="text-sm text-gray-600">{language === 'fr' ? 'Leçons' : 'Lessons'}</p>
                <p className="text-3xl font-bold text-purple-600">{userStats?.lessons_completed || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">XP</p>
                <p className="text-3xl font-bold text-blue-600">{userStats?.total_xp || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">{language === 'fr' ? 'Série' : 'Streak'}</p>
                <p className="text-3xl font-bold text-orange-600">{userStats?.current_streak || 0} 🔥</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Badges by Category */}
        {Object.entries(groupedBadges).map(([category, badges]) => (
          <div key={category} className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              {getCategoryTitle(category)}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {badges.map((badge) => {
                const earned = isBadgeEarned(badge.id);
                const progress = getBadgeProgress(badge);
                const badgeName = language === 'en' ? badge.name_en : badge.name;
                const badgeDesc = language === 'en' ? badge.description_en : badge.description;

                return (
                  <Card
                    key={badge.id}
                    className={`relative overflow-hidden transition-all ${
                      earned
                        ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400 shadow-lg'
                        : 'bg-white/60 border-2 border-gray-200'
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div
                          className={`text-5xl ${
                            earned ? 'filter-none' : 'grayscale opacity-40'
                          }`}
                        >
                          {badge.icon}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-lg">{badgeName}</h3>
                            {earned && (
                              <Badge className="bg-green-500">
                                {language === 'fr' ? 'Obtenu' : 'Earned'}
                              </Badge>
                            )}
                            {!earned && (
                              <Lock className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-3">{badgeDesc}</p>
                          
                          {!earned && (
                            <div className="space-y-1">
                              <Progress value={progress} className="h-2" />
                              <p className="text-xs text-gray-500">
                                {Math.round(progress)}% {language === 'fr' ? 'complété' : 'completed'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BadgesPage;
